import type {
  ProductOptionCreateInput,
  ProductOptionUpdateInput,
} from "@/modules/options/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { productOptionsApi } from "./client";

export const productOptionKeys = {
  all: ["product-options"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["product-options", "list", q] as const,
  detail: (id: string) => ["product-options", "detail", id] as const,
};

export function useProductOptionsQuery(
  query: Partial<OffsetPageQueryDTO & { includeValues?: boolean }> = {},
) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    includeValues: query.includeValues,
  };
  return useQuery({
    queryKey: productOptionKeys.list(q),
    queryFn: () => productOptionsApi.list(q),
  });
}

export function useProductOptionQuery(id: string) {
  return useQuery({
    queryKey: productOptionKeys.detail(id),
    queryFn: () => productOptionsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateProductOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductOptionCreateInput) =>
      productOptionsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productOptionKeys.all });
      toast.success("ສ້າງ Option ສຳເລັດ");
    },
    onError: (error: any) => {
      if (error.message?.includes("already exists")) {
        toast.error("Slug ນີ້ຖືກໃຊ້ແລ້ວ", {
          description: "ກະລຸນາປັບຊື່ພາສາອັງກິດເພື່ອໄດ້ Slug ໃໝ່",
        });
      } else {
        toast.error("ສ້າງ Option ລົ້ມເຫຼວ", {
          description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
        });
      }
    },
  });
}

export function useUpdateProductOption(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductOptionUpdateInput) =>
      productOptionsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productOptionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: productOptionKeys.all });
      toast.success("ແກ້ໄຂ Option ສຳເລັດ");
    },
    onError: (error: any) => {
      if (error.message?.includes("already exists")) {
        toast.error("Slug ນີ້ຖືກໃຊ້ແລ້ວ", {
          description: "ກະລຸນາປັບຊື່ພາສາອັງກິດເພື່ອໄດ້ Slug ໃໝ່",
        });
      } else {
        toast.error("ແກ້ໄຂ Option ລົ້ມເຫຼວ", {
          description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
        });
      }
    },
  });
}

export function useDeleteProductOption() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => productOptionsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productOptionKeys.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}
